import React, { useEffect, useState } from "react";
import { api, downloadPost } from "./api";
import { eastern } from "./RecordScreen";
import { elapsed } from "./Authorization";

/**
 * The pending-authorization queue (build plan §9.1 #4): one row per held
 * medication, longest waiting first, with the elapsed time ticking. Everyone
 * sees it; the actions show by role (Path A: Supervisor; packet, Teams and
 * attach: RN and up). Past Config PendingAuthorizationAlertHours, a row is
 * marked amber.
 */

interface Row {
  medKey: string;
  personKey: string;
  lastName: string;
  firstName: string;
  city: string;
  medication: string;
  source: string;
  controlled: boolean;
  pendingSinceUtc: string;
  authorizationStatus: string;
  decidedAtUtc: string;
  photos: number;
  attested: boolean;
  packetGenerated: boolean;
  teamsOpened: boolean;
  signedPdfAttached: boolean;
  overAlert: boolean;
}

interface Queue {
  rows: Row[];
  alertHours: number;
  teamsLink: string;
  cities: string[];
  can: { authorizeOnSite: boolean; attach: boolean; packet: boolean };
}

const SOURCE: Record<string, string> = { Parent: "Application (no authorization on file)", AddedAtCheckin: "Added at check-in", AddedMidweek: "Added mid-week" };
const STATUSES: [string, string][] = [["Pending", "Pending"], ["NotAuthorized", "Not authorized"], ["AuthorizedToday", "Authorized today"]];
const yn = (b: boolean) => (b ? "Yes" : "—");

export function Pending(): React.ReactElement {
  const [status, setStatus] = useState("Pending");
  const [city, setCity] = useState("");
  const [q, setQ] = useState<Queue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    void api<{ queue: Queue }>("GET", `/api/authorizations?status=${status}&city=${encodeURIComponent(city)}`).then((r) => (r.ok ? (setQ(r.data.queue), setError(null)) : setError(r.data.error ?? "Could not load.")));
  }, [status, city]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Pending authorizations</h2>
      <p className="mb-3 text-sm text-slate-600">Held medications: none is given until it is authorized. {q && <>Marked amber after {q.alertHours} hours.</>}</p>
      <div className="section-card flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="pq-s" className="field-label">Show</label>
          <select id="pq-s" className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        </div>
        <div>
          <label htmlFor="pq-c" className="field-label">City</label>
          <select id="pq-c" className="field-input" value={city} onChange={(e) => setCity(e.target.value)}>
            <option value="">All cities</option>
            {q?.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="(not assigned)">Not assigned</option>
          </select>
        </div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      {q && (
        <div className="section-card overflow-x-auto">
          {q.rows.length === 0 ? (
            <p className="text-sm text-slate-600">Nothing here.</p>
          ) : (
            <table className="w-full min-w-[56rem] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-1 pr-2">Student</th><th className="pr-2">City</th><th className="pr-2">Medication</th><th className="pr-2">Source</th><th className="pr-2">{status === "Pending" ? "Waiting" : "Decided"}</th>
                  <th className="pr-2">Photos</th><th className="pr-2">Attested</th><th className="pr-2">Packet</th><th className="pr-2">Teams</th><th className="pr-2">Signed PDF</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {q.rows.map((r) => (
                  <tr key={r.medKey} className={`border-b border-slate-100 align-top ${r.overAlert ? "bg-amber-50" : ""}`}>
                    <td className="py-1.5 pr-2"><a className="font-medium text-program underline" href={`/station/${encodeURIComponent(r.personKey)}`}>{r.lastName}, {r.firstName}</a><span className="block text-xs text-slate-500">{r.personKey}</span></td>
                    <td className="pr-2">{r.city || "Not assigned"}</td>
                    <td className="pr-2">{r.medication}{r.controlled && <span className="block text-xs text-slate-500">controlled</span>}</td>
                    <td className="pr-2">{SOURCE[r.source] ?? r.source}</td>
                    <td className="pr-2">
                      {status === "Pending" ? (
                        <span className={r.overAlert ? "font-semibold text-amber-900" : ""}>{r.overAlert && "⚠ "}{elapsed(r.pendingSinceUtc, now)}</span>
                      ) : (
                        eastern(r.decidedAtUtc)
                      )}
                      <span className="block text-xs text-slate-500">since {eastern(r.pendingSinceUtc)}</span>
                    </td>
                    <td className="pr-2">{r.photos}</td>
                    <td className="pr-2">{yn(r.attested)}</td>
                    <td className="pr-2">{yn(r.packetGenerated)}</td>
                    <td className="pr-2">{yn(r.teamsOpened)}</td>
                    <td className="pr-2">{yn(r.signedPdfAttached)}</td>
                    <td className="space-x-2 whitespace-nowrap">
                      <a className="text-program underline" href={`/authorizations/${encodeURIComponent(r.medKey)}`}>{status === "Pending" && q.can.authorizeOnSite ? "Path A" : "Open"}</a>
                      {status === "Pending" && q.can.packet && <button type="button" className="text-program underline" onClick={async () => { const e = await downloadPost(`/api/authorizations/${encodeURIComponent(r.medKey)}/packet`, "provider-packet.pdf"); if (e) setError(e); }}>Packet</button>}
                      {status === "Pending" && q.can.attach && q.teamsLink && <a className="text-program underline" href={q.teamsLink} target="_blank" rel="noreferrer noopener" onClick={() => void api("POST", `/api/authorizations/${encodeURIComponent(r.medKey)}/teams`)}>Teams</a>}
                      {status === "Pending" && q.can.attach && <a className="text-program underline" href={`/authorizations/${encodeURIComponent(r.medKey)}#path-b`}>Attach</a>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {q.can.attach && !q.teamsLink && <p className="mt-2 text-xs text-slate-500">Teams link not set in Config (Contacts › Remote medical provider).</p>}
        </div>
      )}
    </section>
  );
}
